import React, { useState } from 'react';
import { isNil, isString, noop, pick } from 'lodash';
import { Tooltip } from '@material-ui/core';
import type { MathJsStatic } from 'mathjs';
import { DividedColumnDetailsDropdownRenderer } from '../DividedColumnDetailsDropdownRenderer';
import DividedColumnDetailsDropdown from '../DividedColumnDetailsDropdown';
import type { ColumnDetailsConfig } from '../DividedColumnDetails.types';
import styles from '../DividedColumnDetails.styles';
import { getDisplayValueForDividedColumnDetails, getTooltipTitle, isFieldInvalid } from '../DividedColumnDetails.utils';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import DependentPropDropdown from '../DependentPropDropdown';
import { isStringArray } from 'src/utils/Primitive/String';
import type { DetachedDropdownProps, DropdownDetails, DropdownHandlers } from './DetachedDropdown.types';

/** Blank column details stub to merge into */
const DEFAULT_COLUMN_DETAILS: ColumnDetailsConfig = {
  item: { text: '', dataIndex: '', editable: false },
  fieldId: '',
  styleId: '',
  data: undefined,
  dropdownOptions: {},
  dependentCalcs: {},
  mathInstance: {} as MathJsStatic,
  updateCoordinateMap: {},
  onCloseCallback: noop,
  onClickCallback: noop,
  openDropdownKey: '',
  handleDropdownChange: noop,
};

/** Assigns all the values in simple to the locations in column */
function mergeDetails(
  details: DropdownDetails,
  handlers: DropdownHandlers,
): {
  merged: ColumnDetailsConfig;
  extraKeys: { renderIdOnly: boolean };
} {
  // props from DividedColumnDetailsDropdownProps, purposefully kept separate
  let extraKeys = {
    allowEmptyOption: false,
    renderIdOnly: false,
  };

  // mutually exclusive details type guarantees if there is a simpleDetails it is of type SimpleDetailsConfig,
  // otherwise the type is guaranteed to be ColumnDetailsConfig without the need for type coercion

  if (details.simpleDetails) {
    const { onEditorChange } = handlers;
    // props from ColumnDetailsConfig root and ColumnDetailsConfig['item']
    const pickedRootKeys = pick(details.simpleDetails, 'openDropdownKey', 'styleId');
    const pickedItemKeys = pick(
      details.simpleDetails,
      'asCsv',
      'multiSelect',
      'dataApi',
      'inputParams',
      'allowEmptyOption',
    );

    const merged = {
      ...DEFAULT_COLUMN_DETAILS,
      ...pickedRootKeys,
      handleDropdownChange: onEditorChange,
      item: {
        ...DEFAULT_COLUMN_DETAILS.item,
        ...pickedItemKeys,
      },
    };

    extraKeys = {
      ...extraKeys,
      ...pick(details.simpleDetails, 'renderIdOnly'),
    };

    return { extraKeys, merged };
  } else {
    extraKeys = {
      ...extraKeys,
      ...pick(details.columnDetails, 'renderIdOnly'),
    };
  }

  // no merging required, details are in the correct output shape already
  return { extraKeys, merged: details.columnDetails };
}

// TODO: better names for this component
// ClickToEditDropdown
// InlineClickToEditDropdown
// InlineDropdown (concise but no details of renderer vs editor though)
// RendererToEditorDropdown
export function DetachedDropdown({
  columnDetails,
  dataQa,
  dependent = false,
  isDisabled,
  id,
  simpleDetails,
  value,
  onEditorChange,
}: DetachedDropdownProps) {
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  const showEditor = (rendererKey: string) => setOpenDropdownKey(rendererKey);
  const hideEditor = () => setOpenDropdownKey(null);

  const details = { simpleDetails, columnDetails } as DropdownDetails;
  const handlers = { onEditorChange };
  const { merged, extraKeys } = mergeDetails(details, handlers);
  const dataApi = merged?.item.dataApi;
  if (!dataApi) {
    return <div />;
  }
  if (!isString(value) && !isStringArray(value)) {
    console.warn(`Possible unexpected behavior for value type other than string/string[]: ${value}`);
  }

  const { data, dropdownOptions, item, handleDropdownChange, styleId } = merged;
  const { allowEmptyOption, asCsv, concatOptionValues, dataIndex, editable, inputParams, mandatory, multiSelect } =
    item;

  const allowEmpty = isNil(allowEmptyOption) ? true : allowEmptyOption;
  const renderId = isNil(extraKeys.renderIdOnly) ? false : extraKeys.renderIdOnly;
  const concatValues = isNil(concatOptionValues) ? false : concatOptionValues;
  // preserving the existing behavior of the value being any
  const coercedValue = value as string | string[];
  const displayValue = getDisplayValueForDividedColumnDetails(dataIndex, coercedValue, multiSelect, dropdownOptions);
  const tooltipTitle = !multiSelect ? '' : getTooltipTitle(displayValue);
  const isActive = openDropdownKey === id;
  // only process if data is provided, otherwise the dataApi is expected to be pre-processed already
  const processedDataApi = data ? processApiParams(dataApi, data) : dataApi;
  const isInvalid = isFieldInvalid(value, merged);
  const showRenderer = isDisabled || !isActive;
  const dataQaAttr = dataQa ?? `select-${id}`;

  let renderedComponent;
  if (showRenderer) {
    renderedComponent = (
      <DividedColumnDetailsDropdownRenderer
        id={id}
        key={id}
        displayValue={displayValue}
        inputParams={inputParams}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        onClick={showEditor}
        tooltip={tooltipTitle}
      />
    );
  } else {
    renderedComponent = (
      <Tooltip
        className={styles.dataItem}
        data-qa="real-multiselect-tooltip"
        key={id}
        title={tooltipTitle}
        placement="right"
      >
        {!dependent ? (
          <DividedColumnDetailsDropdown
            dataQa={dataQaAttr}
            styleId={styleId}
            dataApi={processedDataApi}
            asCsv={asCsv}
            multiSelect={multiSelect}
            value={String(value ?? '')}
            handleDropdownChange={(selection) => handleDropdownChange(selection, item)}
            allowEmptyOption={allowEmpty}
            renderIdOnly={renderId}
            concatOptionValues={concatValues}
            disabled={isDisabled}
            inputParams={inputParams}
            initiallyOpen={true}
            onClose={hideEditor}
          />
        ) : (
          <DependentPropDropdown
            key={id}
            value={String(value ?? '')}
            styleId={styleId}
            dataApi={processedDataApi}
            handleDropdownChange={(selection) => handleDropdownChange(selection, item)}
            dataQa={dataQaAttr}
            mandatory={mandatory}
            allowEmptyOption={allowEmpty}
            concatOptionValues={concatValues}
            editable={editable && !isDisabled}
            initiallyOpen={true}
            onClose={hideEditor}
          />
        )}
      </Tooltip>
    );
  }

  return renderedComponent;
}
